import { useState } from 'react';
import { Sparkles, RefreshCw, AlertTriangle, Wand2 } from 'lucide-react';
import { enhanceItinerary } from '../../api/ai';
import InsightsDisplay from './InsightsDisplay';
import { useTokens } from '../../contexts/TokenContext';
import TokenGateModal from '../ui/TokenGateModal';
import { TokenCost } from '../ui/TokenBadge';
import { TOKEN_COSTS } from '../../constants/tokens';

/**
 * Planner wrapper around InsightsDisplay.
 * Handles the generate / regenerate lifecycle and empty/loading/error states.
 */
export default function InsightsPanel({ itinerary, onInsightsUpdated }) {
  const [insights, setInsights] = useState(itinerary?.ai_insights || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const { canAfford, deductTokens } = useTokens();

  const cost = TOKEN_COSTS.GENERATE_INSIGHTS;

  const handleGenerate = async (force = false) => {
    if (!itinerary?.id) return;
    if (!canAfford(cost)) { setGateOpen(true); return; }
    setLoading(true);
    setError(null);
    try {
      const { insights: newInsights } = await enhanceItinerary(itinerary.id, force);
      deductTokens(cost);
      setInsights(newInsights);
      if (onInsightsUpdated) onInsightsUpdated(newInsights);
    } catch (err) {
      console.error('Failed to generate insights:', err);
      setError(err.message || 'Failed to generate insights');
    }
    setLoading(false);
  };

  return (
    <div>
      {gateOpen && (
        <TokenGateModal
          actionLabel="Trip Insights"
          cost={cost}
          onClose={() => setGateOpen(false)}
        />
      )}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} onRetry={() => handleGenerate(false)} />
        ) : !insights ? (
          <EmptyState onGenerate={() => handleGenerate(false)} cost={cost} />
        ) : (
          <div>
            <div className="flex justify-end mb-3">
              <button
                onClick={() => handleGenerate(true)}
                className="flex items-center gap-1.5 text-xs text-platinum-500 hover:text-coral-500 transition-colors"
                title="Regenerate insights"
              >
                <RefreshCw size={12} />
                Regenerate
              </button>
            </div>
            <InsightsDisplay insights={insights} destination={itinerary?.destination} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── States ─── */

function EmptyState({ onGenerate, cost }) {
  return (
    <div className="rounded-2xl border border-platinum-200/60 bg-gradient-to-br from-coral-50/40 to-columbia-50/40 p-6 text-center">
      <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-3">
        <Wand2 size={22} className="text-coral-500" />
      </div>
      <h3 className="text-base font-semibold text-charcoal-600 mb-1.5">
        Unlock Trip Insights
      </h3>
      <p className="text-xs text-platinum-600 leading-relaxed mb-5 max-w-xs mx-auto">
        Let AI highlight why your itinerary works, surface must-do experiences,
        and tailor your trip to your travel style.
      </p>
      <button
        onClick={onGenerate}
        className="inline-flex items-center gap-2 px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white text-sm font-medium rounded-xl shadow-sm transition-colors"
      >
        <Sparkles size={14} />
        Generate Insights
      </button>
      <div className="mt-3">
        <TokenCost cost={cost} />
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <Sparkles size={18} className="text-coral-500 animate-pulse" />
        <h2 className="text-base font-semibold text-charcoal-600">
          Crafting your insights…
        </h2>
      </div>
      <p className="text-xs text-platinum-600 mb-5">
        Analyzing your trip and pulling together the highlights. This usually
        takes a few seconds.
      </p>
      <div className="space-y-4">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className="rounded-2xl border border-platinum-200/60 bg-white p-5 animate-pulse"
          >
            <div className="h-4 bg-platinum-100 rounded w-1/3 mb-3" />
            <div className="space-y-2">
              <div className="h-3 bg-platinum-100 rounded w-full" />
              <div className="h-3 bg-platinum-100 rounded w-5/6" />
              <div className="h-3 bg-platinum-100 rounded w-4/6" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorState({ error, onRetry }) {
  return (
    <div className="rounded-2xl border border-red-100 bg-red-50/50 p-6 text-center">
      <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-3">
        <AlertTriangle size={22} className="text-red-500" />
      </div>
      <h3 className="text-sm font-semibold text-charcoal-600 mb-1.5">
        Could not generate insights
      </h3>
      <p className="text-xs text-platinum-600 leading-relaxed mb-4 max-w-xs mx-auto">
        {error}
      </p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-platinum-50 text-charcoal-600 text-sm font-medium rounded-xl border border-platinum-200 transition-colors"
      >
        <RefreshCw size={14} />
        Try Again
      </button>
    </div>
  );
}
